import { useEffect } from 'react';
import { Stack, Group, Card, Title, Text, Button, TextInput, Select, ActionIcon, Center, SimpleGrid, Badge, Box, Avatar, Menu } from '@mantine/core';
import { IconArrowLeft, IconSchool, IconTrash, IconDeviceFloppy, IconChevronRight, IconChevronDown, IconCheck, IconUsers, IconUsersGroup, IconPencil } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useTranslation } from 'react-i18next';
import api, { fileUrl } from '../services/api';
import { confirmDelete } from '../lib/confirm';
import { STUDENT_STATUS_KEYS } from '../lib/format';
import { useAuth } from '../contexts/AuthContext';
import { SectionCard } from '../components/SectionCard';
import { Specialty } from '../components/Specialty';
import { BlockLoader } from '../components/BlockLoader';
import { TEACHER_PARAMS } from '../lib/roles';

interface ClassStudent { id: string; fullName: string; status: string; photo?: string | null }
interface ClassGroup { id: string; name: string }
interface ClassDetailData {
  id: string;
  name: string;
  academicYear?: { id: string; name: string } | null;
  homeroomTeacher?: { id: string; fullName: string; specialty?: string | null } | null;
  students: ClassStudent[];
  groups: ClassGroup[];
  _count?: { students: number; groups: number };
}

const apiErr = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

const statusLabel = (t: (k: string) => string, status: string) =>
  (STUDENT_STATUS_KEYS as readonly string[]).includes(status) ? t(`studentStatus.${status}`) : status;

/** O'quvchi statusi ranglari — ro'yxatda holat bir qarashda ajralib tursin. */
const STATUS_COLOR: Record<string, string> = {
  ACTIVE: 'teal', INACTIVE: 'gray', ON_LEAVE: 'yellow', GRADUATED: 'blue', EXPELLED: 'red',
};
const statusColor = (status: string) => STATUS_COLOR[status] ?? 'gray';

/**
 * Statusni o'zgartira oladigan rollar — backend `PATCH /students/:id` uchun AYNI ro'yxat
 * (MANAGERS). O'qituvchi/sinf rahbari 403 oladi, shu sabab ularga tanlagich ko'rsatilmaydi.
 */
const MANAGER_ROLES = ['SUPER_ADMIN', 'DIRECTOR', 'ADMIN', 'ACCOUNTANT', 'RECEPTION'];

/** Sinf detali — ma'lumot + o'quvchilar/guruhlar + tahrirlash + o'chirish (bir sahifada). */
export default function ClassDetail() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canChangeStatus = MANAGER_ROLES.includes(user?.role ?? '');

  const { data: cls, isLoading } = useQuery({
    queryKey: ['class', id],
    queryFn: async () => (await api.get<ClassDetailData>(`/classes/${id}`)).data,
    enabled: !!id,
  });

  // Sinf rahbari uchun o'qituvchilar — Classes.tsx bilan bir xil manba (/staff?role=TEACHER).
  const { data: teachers } = useQuery({
    queryKey: ['teachers'],
    queryFn: async () => (await api.get<{ id: string; fullName: string }[]>('/staff', { params: TEACHER_PARAMS })).data,
  });

  const form = useForm({
    initialValues: { name: '', homeroomTeacherId: '' },
    validate: { name: (v) => (!v ? t('classDetail.nameRequired') : null) },
  });
  useEffect(() => {
    if (cls) form.setValues({ name: cls.name, homeroomTeacherId: cls.homeroomTeacher?.id ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cls?.id]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['class', id] });
    qc.invalidateQueries({ queryKey: ['classes'] });
    qc.invalidateQueries({ queryKey: ['classes-paged'] });
  };

  const saveMut = useMutation({
    mutationFn: (v: typeof form.values) => api.patch(`/classes/${id}`, { name: v.name, homeroomTeacherId: v.homeroomTeacherId || null }),
    onSuccess: () => { notifications.show({ color: 'green', message: t('classDetail.saved') }); invalidate(); },
    onError: (e) => notifications.show({ color: 'red', message: apiErr(e, t('common.error')) }),
  });
  /**
   * T3 — o'quvchi statusini ro'yxatning O'ZIDA o'zgartirish.
   * Yangi endpoint yo'q: yagona yo'l — `PATCH /students/:id { status }`.
   * Sinf ro'yxati `GET /classes/:id` dan keladi, shuning uchun shu query ham,
   * o'quvchilar ro'yxati/kartochkasi ham yangilanadi.
   */
  const statusMut = useMutation({
    mutationFn: (v: { studentId: string; status: string }) => api.patch(`/students/${v.studentId}`, { status: v.status }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('classDetail.statusUpdated') });
      qc.invalidateQueries({ queryKey: ['class', id] });
      // O'quvchilar ro'yxati ham shu kalitda (usePagedQuery('students', ...)) — prefiks bo'yicha mos keladi
      qc.invalidateQueries({ queryKey: ['students'] });
      // O'quvchi kartochkasi (['student', id]) — prefiks bo'yicha hammasi
      qc.invalidateQueries({ queryKey: ['student'] });
    },
    onError: (e) => notifications.show({ color: 'red', message: apiErr(e, t('common.error')) }),
  });

  /** Status o'zgarishi TASDIQ bilan — bexosdan bosilgan menyu yozuvni jimgina o'zgartirmasin. */
  const askStatusChange = (student: ClassStudent, next: string) =>
    modals.openConfirmModal({
      title: t('classDetail.changeStatus'),
      centered: true,
      children: (
        <Text size="sm">
          {t('classDetail.changeStatusConfirm', { name: student.fullName, status: statusLabel(t, next) })}
        </Text>
      ),
      labels: { confirm: t('common.save'), cancel: t('confirm.cancel') },
      onConfirm: () => statusMut.mutate({ studentId: student.id, status: next }),
    });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/classes/${id}`),
    onSuccess: () => { notifications.show({ color: 'green', message: t('classDetail.deleted') }); invalidate(); navigate('/classes'); },
    onError: (e) => notifications.show({ color: 'red', message: apiErr(e, t('common.error')) }),
  });

  if (isLoading) return <BlockLoader variant="card" />;
  if (!cls) return <Center h={300}><Text c="dimmed">{t('classDetail.notFound')}</Text></Center>;

  return (
    <Stack maw={720}>
      <Group>
        <ActionIcon variant="default" size="lg" onClick={() => navigate('/classes')}><IconArrowLeft size={18} /></ActionIcon>
        <Group gap="xs"><IconSchool size={22} color="var(--mantine-color-brand-6)" /><Title order={4}>{cls.name}</Title></Group>
      </Group>

      {/* Umumiy ma'lumot */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Card>
          <Text size="sm" c="dimmed">{t('classDetail.academicYear')}</Text>
          <Text fw={600}>{cls.academicYear?.name ?? '—'}</Text>
        </Card>
        <Card>
          <Text size="sm" c="dimmed">{t('classDetail.homeroomTeacher')}</Text>
          <Text fw={600}>{cls.homeroomTeacher?.fullName ?? '—'}<Specialty value={cls.homeroomTeacher?.specialty} /></Text>
        </Card>
        <Card>
          <Text size="xl" fw={700}>{cls._count?.students ?? cls.students.length}</Text>
          <Text size="sm" c="dimmed">{t('classDetail.studentsCount')}</Text>
        </Card>
        <Card>
          <Text size="xl" fw={700}>{cls._count?.groups ?? cls.groups.length}</Text>
          <Text size="sm" c="dimmed">{t('classDetail.groupsCount')}</Text>
        </Card>
      </SimpleGrid>

      {/* O'quvchilar */}
      <SectionCard
        title={t('classDetail.students')}
        icon={IconUsers}
        right={canChangeStatus && !!cls.students.length ? <Text size="xs" c="dimmed">{t('classDetail.statusHint')}</Text> : <Badge variant="light" color="gray">{cls.students.length}</Badge>}
      >
        {!cls.students.length ? (
          <Text c="dimmed" size="sm">{t('classDetail.noStudents')}</Text>
        ) : (
          <Stack gap="xs">
            {cls.students.map((s) => (
              <Group key={s.id} justify="space-between" wrap="nowrap" p="xs"
                style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 8, cursor: 'pointer' }}
                onClick={() => navigate(`/students/${s.id}`)}>
                <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                  <Avatar size={30} radius="xl" color="brand" src={fileUrl(s.photo)} alt={s.fullName}>
                    {s.fullName[0]?.toUpperCase()}
                  </Avatar>
                  <Text fw={500} size="sm" lineClamp={1}>{s.fullName}</Text>
                </Group>
                <Group gap="xs" wrap="nowrap">
                  {canChangeStatus ? (
                    // Menyu qatorning O'ZIDA — bosilganda o'quvchi kartochkasiga o'tib ketmasin
                    <Box onClick={(e) => e.stopPropagation()}>
                      <Menu withinPortal position="bottom-end" shadow="md">
                        <Menu.Target>
                          <Button
                            size="compact-xs"
                            variant="light"
                            color={statusColor(s.status)}
                            rightSection={<IconChevronDown size={13} />}
                            loading={statusMut.isPending && statusMut.variables?.studentId === s.id}
                          >
                            {statusLabel(t, s.status)}
                          </Button>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Label>{t('classDetail.changeStatus')}</Menu.Label>
                          {STUDENT_STATUS_KEYS.map((k) => (
                            <Menu.Item
                              key={k}
                              disabled={k === s.status}
                              leftSection={k === s.status ? <IconCheck size={14} /> : <Box w={14} />}
                              onClick={() => askStatusChange(s, k)}
                            >
                              {t(`studentStatus.${k}`)}
                            </Menu.Item>
                          ))}
                        </Menu.Dropdown>
                      </Menu>
                    </Box>
                  ) : (
                    <Badge variant="light" color={statusColor(s.status)}>{statusLabel(t, s.status)}</Badge>
                  )}
                  <IconChevronRight size={16} color="var(--mantine-color-dimmed)" />
                </Group>
              </Group>
            ))}
          </Stack>
        )}
      </SectionCard>

      {/* Guruhlar */}
      <SectionCard title={t('classDetail.groups')} icon={IconUsersGroup} right={<Badge variant="light" color="gray">{cls.groups.length}</Badge>}>
        {!cls.groups.length ? (
          <Text c="dimmed" size="sm">{t('classDetail.noGroups')}</Text>
        ) : (
          <Group gap="xs">
            {cls.groups.map((g) => (
              <Badge key={g.id} variant="light" size="lg" style={{ textTransform: 'none' }}>{g.name}</Badge>
            ))}
          </Group>
        )}
      </SectionCard>

      {/* Tahrirlash */}
      <SectionCard title={t('classDetail.edit')} icon={IconPencil}>
        <form onSubmit={form.onSubmit((v) => saveMut.mutate(v))}>
          <Stack>
            <TextInput label={t('classDetail.name')} placeholder="5-A" required {...form.getInputProps('name')} />
            <Select
              label={t('classDetail.homeroomTeacher')}
              placeholder={t('classDetail.homeroomTeacherPlaceholder')}
              data={(teachers ?? []).map((tt) => ({ value: tt.id, label: tt.fullName }))}
              value={form.values.homeroomTeacherId || null}
              onChange={(v) => form.setFieldValue('homeroomTeacherId', v || '')}
              searchable
              clearable
              nothingFoundMessage={t('classDetail.noTeachers')}
              comboboxProps={{ withinPortal: true }}
            />
            <Box>
              <Button type="submit" leftSection={<IconDeviceFloppy size={16} />} loading={saveMut.isPending}>{t('common.save')}</Button>
            </Box>
          </Stack>
        </form>
      </SectionCard>

      {/* O'chirish */}
      <Card withBorder style={{ borderColor: 'var(--mantine-color-red-3)' }}>
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm" c="dimmed">{t('classDetail.deleteHint')}</Text>
          <Button color="red" variant="light" leftSection={<IconTrash size={16} />} onClick={() => confirmDelete(cls.name, () => deleteMut.mutate())} loading={deleteMut.isPending}>
            {t('common.delete')}
          </Button>
        </Group>
      </Card>
    </Stack>
  );
}
